import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, Trash2, Star, PackageOpen, MapPin } from 'lucide-react';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo, SeloInativo } from '../../lib/produto-inativo';
import { FotoProduto } from '../../components/FotoProduto';
import { contar } from '@fiodecobre/shared';
import { Modal } from '../../components/Modal';
import { ComboBox, type OpcaoCombo } from '../../components/ComboBox';

// Transferir Estoque — mover mercadoria de prateleira em LOTE ([DA-449]).
//
// POR QUE TELA PROPRIA, e nao um botao na `Estoque por Prateleira` (que foi o que propus primeiro).
// O Ruan corrigiu o alvo: *"as vezes decidimos mudar os produtos de prateleira, para otimizar o
// espaco da loja"*. Isso nao e mover UM produto — e esvaziar uma prateleira inteira pra outra. Pela
// planilha de contagem seriam 35 operacoes uma a uma pra remanejar a `B02`; o recurso existiria e
// ninguem usaria. A operacao e episodica e grande, entao a tela e a unidade de trabalho: monta-se a
// leva, confere-se, confirma-se de uma vez.
//
// A FORMA: duas colunas. Esquerda = de onde tirar (a prateleira e quem mora nela). Direita = a leva
// que vai sair, o destino e o botao. E o desenho do movimento fisico — pilha de um lado, carrinho do
// outro — e o mesmo par (escolhe o lugar / ve quem mora nele) da `Localizacoes por Produtos`
// ([DA-327]), que o estoquista ja conhece.
//
// DUAS ENTRADAS PRA MESMA LEVA, pedido do Ruan: pela prateleira (marca varios de uma vez) e **por
// codigo** (digita, Enter, entrou). A segunda e o caso de mover pecas especificas que nao estao
// todas juntas — e por causa dela a **origem e por LINHA**, nao da tela: cada produto sai do lugar
// dele. O destino e que e unico; e pra onde a leva esta indo.

interface Lugar { id: number; codigo: string; descricao: string | null; produtos_ativos: number }

interface LugarDoProduto {
  localizacao_id: number;
  codigo: string;
  quantidade: string | null;
  principal: boolean;
}

/**
 * Os dois fatores de conversao que a casa guarda, que respondem perguntas DIFERENTES ([DA-586]).
 * Vem juntos porque quem le a linha nao sabe de qual deles precisa — sabe que o "1" da tela pode
 * significar um rolo de cem metros.
 */
interface Conversao {
  /** COMPRA -> VENDA: cano de cobre entra em KG e sai em MT (x7,14). 4 produtos ativos. */
  fator_conversao: string | null;
  unidade_compra: string | null;
  /** PAI -> FILHO: o pacote que se abre (1 CX = 100 UN). 52 vinculos. */
  frac_fator: string | null;
  frac_filho_unidade: string | null;
  frac_filho_codigo: string | null;
}

interface ProdutoBusca extends Conversao {
  produto_id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  estoque_atual: string;
  unidade_sigla: string | null;
  lugares: LugarDoProduto[];
  status?: string | null;
}

/** Uma linha do que mora na prateleira escolhida (vem do `/localizacoes/vinculos`). */
interface Vinculo extends Conversao {
  produto_id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  localizacao_id: number;
  localizacao_codigo: string;
  principal: boolean;
  quantidade: string | null;
  lugares_do_produto: number;
  unidade_sigla: string | null;
}

/** Uma linha da leva. `origens` sao os lugares do produto — a origem e escolhivel por linha. */
interface ItemLeva extends Conversao {
  produto_id: number;
  codigo_interno: string | null;
  descricao: string;
  foto_url: string | null;
  unidade_sigla: string | null;
  origens: LugarDoProduto[];
  origem_id: number;
  /** Texto livre enquanto digita; vira numero so no envio (padrao dos campos numericos da casa). */
  quantidade: string;
}

interface Resultado {
  destino_codigo: string;
  movidos: Array<{ produto_id: number; codigo_interno: string | null; descricao: string; origem_codigo: string; quantidade: number }>;
  criados: number;
  estrelas_movidas?: Array<{ produto_id: number; codigo_interno: string | null; descricao: string; de: string }>;
}

/** `numeric` do Postgres chega como string ("3.000"). `parseQtd` viraria 3000 — usar Number(). */
const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const fmt = (v: unknown) => String(num(v)).replace('.', ',');
const paraNumero = (t: string) => Number(t.trim().replace(',', '.'));

const ALERTA = { background: '#f97316', color: '#000', fontWeight: 700, borderRadius: 3, padding: '1px 6px' };

// Os dois lados sao PAINEIS de altura igual, e a altura igual e a decisao — nao a moldura. A 1a
// versao empilhava campos soltos no fundo da pagina: com a prateleira ainda nao escolhida, a
// esquerda ficava vazia e a tela abria com metade deserta. Painel que ja tem forma antes de ter
// conteudo da a tela um formato desde o primeiro segundo, e cada vazio vira convite (ver os
// blocos `Vazio` abaixo) em vez de buraco.
const PAINEL: React.CSSProperties = {
  background: 'var(--panel-bg)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  boxShadow: '0 1px 2px rgba(0,0,0,.06)',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 470,
  // Teto na altura da JANELA, nao no conteudo: sem isso o painel cresce com a lista (uma prateleira
  // com 54 produtos empurra a pagina inteira) e quem rola e o navegador — o rodape com o botao
  // Transferir sai da tela. Com o teto, o topo e o rodape ficam parados e so o miolo rola, que e a
  // regra da casa pra tela que cabe sem barra de rolagem.
  maxHeight: 'calc(100vh - 235px)',
  // `visible` de proposito: o dropdown da busca sai do painel. O arredondamento fica no proprio
  // topo/rodape, que sao os unicos que encostam na borda.
  overflow: 'visible',
};
const FAIXA: React.CSSProperties = { padding: '11px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 };
// `display:flex` pra o bloco de vazio poder esticar com `flex:1` em vez de `height:100%` — com a
// altura fixa em 100% ele somava ao padding e nascia uma barra de rolagem no painel VAZIO, que e
// justamente o estado em que nao ha o que rolar.
const CORPO: React.CSSProperties = { padding: '12px 14px', flex: 1, overflowY: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' };
const RODAPE: React.CSSProperties = {
  padding: '11px 14px', borderTop: '1px solid var(--border)', background: 'rgba(0,0,0,.035)',
  display: 'flex', alignItems: 'center', gap: 10, borderRadius: '0 0 10px 10px',
};
const ROTULO: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', opacity: 0.62 };

// Descricao numa coluna estreita: uma linha so, com reticencias e o texto inteiro no `title`.
// `maxWidth: 0` e o truque que faz o `text-overflow` valer dentro de `<table>` — sem ele a celula
// ignora a largura e o nome quebra em cinco linhas, que foi como a lista da esquerda nasceu.
const DESCRICAO_CURTA: React.CSSProperties = { maxWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

/**
 * O selo de conversao ([DA-586]). Cinza de proposito: e' INFORMACAO, nao alarme nem alerta — a
 * transferencia continua movendo o mesmo produto entre prateleiras e o total continua sem mudar. A
 * cor de estado (laranja/amarelo) desta tela ja significa outra coisa (`ALERTA`, a estrela), e
 * reusar o mesmo amarelo aqui faria o selo afirmar urgencia que ele nao tem — a regra de "conferir
 * o que a cor JA significa naquela tela" antes de escolher.
 *
 * Silencioso quando nao ha fator, que e' o caso da esmagadora maioria (4 produtos com fator de
 * compra e 52 vinculos de pacote, num acervo de 1.503 ativos). Selo que aparece sempre vira papel
 * de parede e para de ser lido.
 */
function SeloFator({ item, unidade }: { item: Conversao; unidade?: string | null }) {
  const partes: string[] = [];
  const frac = Number(item.frac_fator);
  if (Number.isFinite(frac) && frac > 0) {
    partes.push(`1 ${unidade ?? 'un'} = ${fmt(frac)} ${item.frac_filho_unidade ?? ''}`.trim());
  }
  const compra = Number(item.fator_conversao);
  if (Number.isFinite(compra) && compra > 0 && item.unidade_compra) {
    partes.push(`compra ${item.unidade_compra}: 1 = ${fmt(compra)} ${unidade ?? ''}`.trim());
  }
  if (partes.length === 0) return null;
  return (
    <span
      title="Fator de conversao cadastrado neste produto. A transferencia move o produto como ele e — este numero e so pra conferencia."
      style={{
        marginLeft: 6, fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap',
        border: '1px solid var(--border)', borderRadius: 3, padding: '0 4px', opacity: 0.8,
      }}
    >
      {partes.join(' · ')}
    </span>
  );
}

/** O convite que ocupa o lugar do vazio. Tela vazia e momento de dizer o que fazer, nao de calar. */
function Vazio({ icone, children }: { icone: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: 0.55, fontSize: 13, textAlign: 'center', padding: 20 }}>
      {icone}
      <div style={{ maxWidth: 290 }}>{children}</div>
    </div>
  );
}

export function TransferirEstoque() {
  const toast = useToast();
  const dialog = useDialog();

  const [lugares, setLugares] = useState<Lugar[]>([]);
  const [origemLugarId, setOrigemLugarId] = useState<number | null>(null);
  const [moradores, setMoradores] = useState<Vinculo[]>([]);
  const [carregandoMoradores, setCarregandoMoradores] = useState(false);
  const [marcados, setMarcados] = useState<Set<number>>(new Set());

  const [termo, setTermo] = useState('');
  const [sugestoes, setSugestoes] = useState<ProdutoBusca[]>([]);
  const [buscando, setBuscando] = useState(false);
  const campoCodigo = useRef<HTMLInputElement>(null);

  // ─── A busca da ORIGEM ([DA-586]) ────────────────────────────────────────────────────────────
  // Ate aqui a coluna da esquerda so respondia "quem mora nesta prateleira?", e pra perguntar isso
  // era preciso ja saber a prateleira. Quem nao sabe onde a mercadoria esta — que e o caso comum de
  // quem vai remanejar — nao tinha por onde comecar: a busca da direita existia, mas ela ADICIONA a
  // leva, e portanto ja e a decisao final, nao a procura.
  //
  // O campo faz as duas coisas com o mesmo texto, na ordem em que a duvida aparece: filtra o que
  // esta aberto e, quando nao acha nada ali, diz onde a coisa ESTA. A segunda metade e a que paga —
  // ela responde a pergunta que a tela nao respondia.
  const [filtroOrigem, setFiltroOrigem] = useState('');
  const [achadosFora, setAchadosFora] = useState<ProdutoBusca[]>([]);

  const [leva, setLeva] = useState<ItemLeva[]>([]);
  const [destinoId, setDestinoId] = useState<number | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  /** Onde o dropdown da busca deve pousar. Ver o `position: fixed` mais abaixo. */
  const [rectBusca, setRectBusca] = useState<DOMRect | null>(null);

  useEffect(() => {
    api.get<Lugar[]>('/api/localizacoes').then(setLugares).catch(() => setLugares([]));
  }, []);

  // Quem mora na prateleira escolhida. Sem `so_multi_lugar`: aqui o produto de um lugar so e
  // exatamente quem mais precisa mudar de endereco — filtra-lo esconderia a maioria da loja.
  useEffect(() => {
    if (!origemLugarId) { setMoradores([]); setMarcados(new Set()); return; }
    let cancelado = false;
    setCarregandoMoradores(true);
    setMarcados(new Set());
    api.get<Vinculo[]>(`/api/localizacoes/vinculos?localizacao_id=${origemLugarId}`)
      .then((r) => { if (!cancelado) setMoradores(r); })
      .catch(() => { if (!cancelado) setMoradores([]); })
      .finally(() => { if (!cancelado) setCarregandoMoradores(false); });
    return () => { cancelado = true; };
  }, [origemLugarId]);

  // Busca por codigo com atraso — cada tecla vira consulta no banco sem isso.
  useEffect(() => {
    const t = termo.trim();
    if (t.length < 2) { setSugestoes([]); return; }
    let cancelado = false;
    setBuscando(true);
    const timer = setTimeout(() => {
      api.get<ProdutoBusca[]>(`/api/estoque/transferencias/buscar?termo=${encodeURIComponent(t)}`)
        .then((r) => { if (!cancelado) setSugestoes(r); })
        .catch(() => { if (!cancelado) setSugestoes([]); })
        .finally(() => { if (!cancelado) setBuscando(false); });
    }, 300);
    return () => { cancelado = true; clearTimeout(timer); };
  }, [termo]);

  /** O que sobra da prateleira aberta depois do filtro. Client-side: a lista ja esta toda aqui. */
  const moradoresFiltrados = useMemo(() => {
    const t = filtroOrigem.trim().toLowerCase();
    if (!t) return moradores;
    return moradores.filter(
      (m) => (m.codigo_interno ?? '').toLowerCase().includes(t) || m.descricao.toLowerCase().includes(t),
    );
  }, [moradores, filtroOrigem]);

  // A metade que responde "entao onde ESTA?". So dispara quando o filtro nao achou nada na
  // prateleira aberta (ou quando nenhuma esta aberta) — enquanto a resposta esta na tela, perguntar
  // ao servidor seria gastar consulta pra repetir o que o operador ja ve.
  //
  // Sem isso a tela teria o modo de falha do [DA-468]: filtro que nao casa devolve lista vazia, e
  // vazio le como "nao existe" — quando a verdade e "existe, mora noutro lugar".
  useEffect(() => {
    const t = filtroOrigem.trim();
    if (t.length < 2 || moradoresFiltrados.length > 0) { setAchadosFora([]); return; }
    let cancelado = false;
    const timer = setTimeout(() => {
      api.get<ProdutoBusca[]>(`/api/estoque/transferencias/buscar?termo=${encodeURIComponent(t)}`)
        .then((r) => { if (!cancelado) setAchadosFora(r); })
        .catch(() => { if (!cancelado) setAchadosFora([]); });
    }, 300);
    return () => { cancelado = true; clearTimeout(timer); };
  }, [filtroOrigem, moradoresFiltrados.length]);

  // O dropdown da busca sai do painel, entao ele e `fixed` ancorado no rect do campo — convencao da
  // casa pra menu dentro de container que rola (`absolute` seria cortado pelo corpo do painel).
  useEffect(() => {
    if (sugestoes.length > 0) setRectBusca(campoCodigo.current?.getBoundingClientRect() ?? null);
  }, [sugestoes]);

  const naLeva = useMemo(() => new Set(leva.map((i) => `${i.produto_id}-${i.origem_id}`)), [leva]);

  /** De onde a leva esta saindo, pro de-para do topo. Varias origens viram "N lugares". */
  const rotuloOrigem = useMemo(() => {
    const codigos = new Set<string>();
    for (const it of leva) {
      const o = it.origens.find((x) => x.localizacao_id === it.origem_id);
      if (o) codigos.add(o.codigo);
    }
    if (codigos.size === 0) return null;
    if (codigos.size === 1) return [...codigos][0]!;
    return `${codigos.size} lugares`;
  }, [leva]);

  /**
   * Poe um produto na leva. A origem entra ja escolhida: o lugar preferido quando ha um (o clique
   * veio de uma prateleira), senao o mais cheio — mesma ordem que o painel de reposicao usa.
   *
   * Codigo repetido NAO duplica linha: soma na que ja esta la. Duas linhas do mesmo par mandariam
   * dois movimentos da mesma prateleira e a pessoa nao veria o total real saindo dali.
   */
  function adicionar(p: ProdutoBusca, preferido?: number) {
    if (produtoInativo(p)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return; }
    const comSaldo = p.lugares.filter((l) => l.quantidade !== null);
    if (p.lugares.length === 0) {
      toast.erro(`${p.codigo_interno ?? p.descricao} nao tem endereco nenhum — cadastre o lugar na ficha do produto antes de mover.`);
      return;
    }
    if (comSaldo.length === 0) {
      toast.erro(`${p.codigo_interno ?? p.descricao}: nenhuma prateleira dele foi contada ainda — conte antes de mover.`);
      return;
    }
    const origem = (preferido && comSaldo.some((l) => l.localizacao_id === preferido))
      ? preferido
      : comSaldo[0]!.localizacao_id;

    setLeva((atual) => {
      const i = atual.findIndex((x) => x.produto_id === p.produto_id && x.origem_id === origem);
      const lug = comSaldo.find((l) => l.localizacao_id === origem)!;
      const anterior = i >= 0 ? atual[i] : null;
      if (anterior) {
        const somado = Math.min(num(anterior.quantidade.replace(',', '.')) + 1, num(lug.quantidade));
        const copia = [...atual];
        copia[i] = { ...anterior, quantidade: fmt(somado) };
        toast.aviso(`${p.codigo_interno ?? p.descricao} ja estava na lista — somei.`);
        return copia;
      }
      return [...atual, {
        produto_id: p.produto_id,
        codigo_interno: p.codigo_interno,
        descricao: p.descricao,
        foto_url: p.foto_url,
        unidade_sigla: p.unidade_sigla,
        fator_conversao: p.fator_conversao,
        unidade_compra: p.unidade_compra,
        frac_fator: p.frac_fator,
        frac_filho_unidade: p.frac_filho_unidade,
        frac_filho_codigo: p.frac_filho_codigo,
        origens: comSaldo,
        origem_id: origem,
        // Pre-preenchido com TUDO que ha no lugar: o caso comum do remanejamento e levar a
        // prateleira inteira. Quem vai mover so uma parte edita o numero; quem vai mover tudo
        // confirma. O contrario (nascer vazio) faria a pessoa digitar 30 numeros que o sistema ja
        // sabe.
        quantidade: fmt(lug.quantidade),
      }];
    });
  }

  /** Adiciona da coluna da esquerda — o vinculo ja traz a origem (a prateleira aberta). */
  function adicionarVinculos(vs: Vinculo[]) {
    // Contado em ZERO entra (pedido do Ruan, 17/10/2026): transferir o produto zerado muda o
    // endereco dele no cadastro. So o `—` (nunca contado) fica de fora.
    const uteis = vs.filter((v) => v.quantidade !== null);
    const pulados = vs.length - uteis.length;
    if (uteis.length === 0) {
      toast.erro(pulados > 0 ? 'Nenhum dos marcados foi contado nesta prateleira.' : 'Nada marcado.');
      return;
    }
    setLeva((atual) => {
      const copia = [...atual];
      for (const v of uteis) {
        if (copia.some((x) => x.produto_id === v.produto_id && x.origem_id === v.localizacao_id)) continue;
        copia.push({
          produto_id: v.produto_id,
          codigo_interno: v.codigo_interno,
          descricao: v.descricao,
          foto_url: v.foto_url,
          // A unidade passou a vir do `/vinculos` junto com os fatores ([DA-586]) — antes era `null`
          // aqui, e a linha adicionada pela ESQUERDA nascia sem unidade enquanto a mesma linha vinda
          // da busca da direita tinha. Duas portas pro mesmo estado tem que devolver a mesma coisa.
          unidade_sigla: v.unidade_sigla,
          fator_conversao: v.fator_conversao,
          unidade_compra: v.unidade_compra,
          frac_fator: v.frac_fator,
          frac_filho_unidade: v.frac_filho_unidade,
          frac_filho_codigo: v.frac_filho_codigo,
          origens: [{ localizacao_id: v.localizacao_id, codigo: v.localizacao_codigo, quantidade: v.quantidade, principal: v.principal }],
          origem_id: v.localizacao_id,
          quantidade: fmt(v.quantidade),
        });
      }
      return copia;
    });
    if (pulados > 0) toast.aviso(`${pulados} sem contagem ficaram de fora — conte antes de mover.`);
    setMarcados(new Set());
  }

  function trocarOrigem(idx: number, novaOrigem: number) {
    setLeva((atual) => atual.map((it, i) => {
      if (i !== idx) return it;
      const lug = it.origens.find((o) => o.localizacao_id === novaOrigem);
      return { ...it, origem_id: novaOrigem, quantidade: lug ? fmt(lug.quantidade) : it.quantidade };
    }));
  }

  const saldoDaLinha = (it: ItemLeva) => num(it.origens.find((o) => o.localizacao_id === it.origem_id)?.quantidade);
  /** Zero so vale quando a origem esta zerada: ai a linha muda o ENDERECO, sem mercadoria. */
  const qtdInvalida = (q: number, saldo: number) => !Number.isFinite(q) || q < 0 || q > saldo || (q === 0 && saldo > 0);

  // As tres perguntas que decidem se o botao acende. Separadas de proposito: cada uma vira uma
  // mensagem propria abaixo, porque "nao da pra transferir" sem dizer O QUE falta e o defeito que a
  // tela nao pode ter.
  const semDestino = !destinoId;
  const destinoNaLeva = destinoId != null && leva.some((it) => it.origem_id === destinoId);
  const linhasInvalidas = leva.filter((it) => qtdInvalida(paraNumero(it.quantidade), saldoDaLinha(it)));
  const podeEnviar = leva.length > 0 && !semDestino && !destinoNaLeva && linhasInvalidas.length === 0 && !enviando;

  // Toda transferencia e DEFINITIVA (06/10/2026): linha que leva tudo apaga o endereco velho e, se
  // ele era a vitrine, a estrela vai junto. A tela diz antes de confirmar quantas vitrines mudam.
  const estrelasQueMudam = useMemo(() => leva.filter((it) => {
    const org = it.origens.find((o) => o.localizacao_id === it.origem_id);
    if (!org?.principal) return false;
    const q = paraNumero(it.quantidade);
    return Number.isFinite(q) && q >= num(org.quantidade);
  }), [leva]);
  const linhasParciais = leva.filter((it) => {
    const q = paraNumero(it.quantidade);
    return Number.isFinite(q) && q > 0 && q < saldoDaLinha(it);
  });

  async function transferir() {
    if (!destinoId) return;
    // Confirmacao com o de-para escrito (pedido do Ruan): a mudanca apaga endereco e move vitrine,
    // e desfazer e outra transferencia na mao.
    const ok = await dialog.confirmar(
      `Voce esta transferindo ${contar(leva.length, 'produto')} da prateleira ${rotuloOrigem ?? '—'} para a prateleira ${lugarDe(destinoId)?.codigo ?? '—'}.`,
      { titulo: 'Confirmar transferencia', textoConfirmar: 'Confirmar', textoCancelar: 'Cancelar' },
    );
    if (!ok) return;
    setEnviando(true);
    try {
      const r = await api.post<Resultado>('/api/estoque/transferencias', {
        destino_id: destinoId,
        itens: leva.map((it) => ({ produto_id: it.produto_id, origem_id: it.origem_id, quantidade: paraNumero(it.quantidade) })),
      });
      setResultado(r);
      setLeva([]);
      setDestinoId(null);
      // A prateleira aberta a esquerda acabou de mudar — reler pra nao mostrar saldo velho.
      if (origemLugarId) {
        api.get<Vinculo[]>(`/api/localizacoes/vinculos?localizacao_id=${origemLugarId}`).then(setMoradores).catch(() => { /* a leva ja foi; lista velha nao trava nada */ });
      }
    } catch (e: any) {
      toast.erro(e?.corpo?.erro ?? e?.message ?? 'Nao foi possivel transferir.');
    } finally {
      setEnviando(false);
    }
  }

  const lugarDe = (id: number | null) => lugares.find((l) => l.id === id);
  const opcoesOrigem: OpcaoCombo[] = useMemo(
    () => lugares.map((l) => ({ value: l.id, label: `${l.codigo}${l.descricao ? ` — ${l.descricao}` : ''} (${l.produtos_ativos})` })),
    [lugares],
  );
  const opcoesDestino: OpcaoCombo[] = useMemo(
    () => lugares.map((l) => ({ value: l.id, label: `${l.codigo}${l.descricao ? ` — ${l.descricao}` : ''}` })),
    [lugares],
  );

  return (
    <div>
      <div className="pagina-header">
        <h2 className="pagina-titulo">
          <ArrowRight size={20} style={{ display: 'inline', marginRight: 6, marginBottom: -3 }} /> Transferir Estoque
        </h2>
      </div>
      <div style={{ display: 'flex', gap: 16, alignItems: 'stretch', flexWrap: 'wrap' }}>
        {/* ─── ESQUERDA: de onde tirar ─────────────────────────────────────────────
            Nasceu estreita (340px, [DA-449]) pra largura ficar com a leva. Foi a 488 por decisao
            do Ruan (06/10/2026, [DA-794] p.5): com a coluna Foto a Descricao caiu pra 95px e
            cortava o que distingue um produto do outro; ele pediu a Descricao maior tirando do
            painel da direita — 25%, depois o dobro (238px). */}
        <div style={{ ...PAINEL, flex: '0 1 488px', minWidth: 300 }}>
          <div style={FAIXA}>
            <span style={ROTULO}>De onde sai</span>
            {origemLugarId && moradores.length > 0 && (
              <span className="muted" style={{ fontSize: 12, marginLeft: 'auto' }}>
                {/* Com filtro em pe o contador conta o RECORTE, e diz de quanto — senao ele afirma
                    que a prateleira encolheu. Filtro que esconde tem que se anunciar ([DA-363]). */}
                {filtroOrigem.trim()
                  ? `${moradoresFiltrados.length} de ${moradores.length}`
                  : `${moradores.length} produto(s)`}
              </span>
            )}
          </div>

          <div style={CORPO}>
            {/* Combo com busca (pedido do Ruan, 06/10/2026): sao 157 prateleiras na ordem da fila
                manual, e o <select> nativo obrigava a rolar ate achar — a GOND01 mora no fim, longe
                das G. Digitar filtra em qualquer posicao do rotulo. */}
            <ComboBox
              opcoes={opcoesOrigem}
              value={origemLugarId}
              onChange={(v) => setOrigemLugarId(v === '' ? null : Number(v))}
              placeholder="Escolha a prateleira (digite pra buscar)..."
            />

            <input
              type="search"
              className="busca"
              placeholder="Pesquisar produto..."
              value={filtroOrigem}
              onChange={(e) => setFiltroOrigem(e.target.value)}
              style={{ width: '100%', marginTop: 8 }}
            />

            {/* "Nao esta aqui" nunca vai sozinho — vai com o endereco. Lista vazia sem isto le como
                "o produto nao existe", que e o modo de falha do [DA-468]. Clicar ABRE a prateleira
                onde ele mora: o filtro fica de pe e a lista ja chega recortada nele. */}
            {achadosFora.length > 0 && (
              <div style={{ marginTop: 8, border: '1px solid var(--border)', borderRadius: 6, padding: '7px 9px' }}>
                {/* Frase NAO usa o estilo `ROTULO` — ele e caixa-alta com `letter-spacing`, feito
                    pra rotulo de uma ou duas palavras ("De onde sai"). Uma sentenca inteira ali sai
                    gritando e fica mais dificil de ler que o texto comum. */}
                <div className="muted" style={{ fontSize: 11.5, marginBottom: 5 }}>Nao esta nesta prateleira. Esta em:</div>
                {achadosFora.slice(0, 6).map((p) => (
                  <div key={p.produto_id} style={{ marginBottom: 5 }}>
                    <div style={{ fontSize: 12.5, display: 'flex', gap: 6, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span className="text-gray-500 font-bold no-underline">{p.codigo_interno}</span>
                      <span style={{ flex: 1, minWidth: 0 }}>{produtoInativo(p) && <SeloInativo />}{p.descricao}</span>
                      <SeloFator item={p} unidade={p.unidade_sigla} />
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 3 }}>
                      {p.lugares.length === 0 ? (
                        <span className="muted" style={{ fontSize: 11.5 }}>sem endereco cadastrado</span>
                      ) : p.lugares.map((l) => (
                        <button
                          key={l.localizacao_id}
                          type="button"
                          className="btn-secundario"
                          onClick={() => setOrigemLugarId(l.localizacao_id)}
                          style={{ padding: '2px 7px', fontSize: 11.5 }}
                          title={`Abrir a prateleira ${l.codigo}`}
                        >
                          {l.codigo}: {l.quantidade === null ? '—' : fmt(l.quantidade)}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!origemLugarId ? (
              <Vazio icone={<MapPin size={26} />}>
                {filtroOrigem.trim().length >= 2
                  ? 'Escolha uma das prateleiras acima, ou abra uma pela lista.'
                  : 'Escolha uma prateleira, ou pesquise o produto pra descobrir onde ele mora.'}
              </Vazio>
            ) : carregandoMoradores ? (
              <p className="muted" style={{ marginTop: 14 }}>Carregando...</p>
            ) : moradores.length === 0 ? (
              <Vazio icone={<MapPin size={26} />}>Nenhum produto ativo nesta prateleira.</Vazio>
            ) : moradoresFiltrados.length === 0 ? (
              <Vazio icone={<MapPin size={26} />}>
                Nada com "{filtroOrigem.trim()}" nesta prateleira.
              </Vazio>
            ) : (
              <div style={{ marginTop: 10 }}>
                  {/* Layout FIXO: no automatico a tabela repartia qualquer largura nova entre as
                      outras colunas — 35px a mais no painel davam 6px a Descricao (medido). Fixo,
                      as quatro colunas ficam com o numero escrito e a Descricao leva o resto. */}
                  <table className="tabela" style={{ margin: 0, tableLayout: 'fixed', width: '100%' }}>
                    <thead>
                      <tr>
                        {/* A sigla da unidade custou largura, e ela saiu de onde sobrava, nao da
                            Descricao: Codigo 74 -> 62 (os codigos tem 4 digitos) e Tem 66 -> 78.
                            Soma identica a de antes, entao a Descricao nao perdeu um pixel — na
                            1a tentativa eu tirei os 18px dela e "FLEX 6MM PTO 100M" virou
                            "FLEX 6MM P...". So a tela publicada mostra isso. */}
                        <th style={{ width: 34 }}></th>
                        {/* 68 no layout fixo: com 62 o rotulo "CODIGO" encostava no "FOTO" (visto no print). */}
                        <th style={{ width: 68 }}>Codigo</th>
                        {/* Foto pedida pelo Ruan (06/10/2026). Miniatura de 32 — a de 40 da leva ao
                            lado comeria mais um tanto da Descricao num painel de 340px. */}
                        <th style={{ width: 42, padding: '10px 4px', textAlign: 'center' }}>Foto</th>
                        <th>Descricao</th>
                        {/* 76 e' o minimo pra "1000 UN" (49px de texto + 24 de padding) */}
                        <th style={{ width: 76, textAlign: 'right' }}>Tem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {moradoresFiltrados.map((m) => {
                        const semContagem = m.quantidade === null;
                        const jaNaLeva = naLeva.has(`${m.produto_id}-${m.localizacao_id}`);
                        return (
                          <tr
                            key={m.produto_id}
                            style={jaNaLeva ? { opacity: 0.45 } : undefined}
                            title={jaNaLeva ? 'Ja esta na lista ao lado' : semContagem ? 'Sem contagem nesta prateleira — conte antes de mover' : undefined}
                          >
                            <td>
                              <input
                                type="checkbox"
                                checked={marcados.has(m.produto_id)}
                                disabled={semContagem || jaNaLeva}
                                onChange={(e) => setMarcados((s) => {
                                  const n = new Set(s);
                                  if (e.target.checked) n.add(m.produto_id); else n.delete(m.produto_id);
                                  return n;
                                })}
                              />
                            </td>
                            <td className="text-gray-500 font-bold no-underline">{m.codigo_interno}</td>
                            <td style={{ padding: '3px 4px' }}>
                              {m.foto_url
                                ? <FotoProduto url={m.foto_url} tam={32} />
                                : <div style={{ width: 32, height: 32, border: '1px dashed var(--border)', borderRadius: 4 }} title="Sem foto" />}
                            </td>
                            <td style={DESCRICAO_CURTA} title={m.descricao}>
                              {m.descricao}
                              {m.principal && (
                                <span title="Esta e a vitrine deste produto" style={{ background: '#fde68a', color: '#000', borderRadius: 3, padding: '1px 3px', marginLeft: 5, display: 'inline-flex' }}>
                                  <Star size={10} fill="#000" />
                                </span>
                              )}
                            </td>
                            {/* A UNIDADE cola no numero ([DA-586]). Nesta coluna de 84px nao cabe o
                                selo de fator, e sem a sigla o "1" nao diz se e um rolo de cem metros
                                ou um metro — que foi exatamente a confusao que abriu esta onda. A
                                sigla cabe sempre e responde a pergunta antes dela ser feita. */}
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {semContagem ? <span className="muted">—</span> : (
                                <>
                                  {fmt(m.quantidade)}
                                  {m.unidade_sigla && (
                                    <span className="muted" style={{ fontSize: 10.5, marginLeft: 3 }}>{m.unidade_sigla}</span>
                                  )}
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
              </div>
            )}
          </div>

          <div style={RODAPE}>
            {/* Marca o que esta A VISTA, nao a prateleira inteira ([DA-586]). Com filtro em pe, o
                botao que marcasse `moradores` poria na leva linhas que o operador nao esta vendo —
                e ele so descobriria no painel da direita, depois de confirmar. O rotulo muda junto:
                botao que faz outra coisa nao pode ter o mesmo nome. */}
            <button
              type="button" className="btn-secundario"
              onClick={() => setMarcados(new Set(moradoresFiltrados.filter((m) => m.quantidade !== null).map((m) => m.produto_id)))}
              disabled={moradoresFiltrados.length === 0}
              // `nowrap` obrigatorio: botao e item de flex e encolhe ate o conteudo caber, entao o
              // rotulo mais longo quebrava em duas linhas dentro do proprio botao (visto na tela, o
              // portao passa verde). Rotulo curto pelo mesmo motivo — sao tres botoes em 340px.
              style={{ whiteSpace: 'nowrap' }}
            >
              {filtroOrigem.trim() ? 'Marcar filtrados' : 'Marcar todos'}
            </button>
            <button
              type="button" className="btn-secundario"
              onClick={() => setMarcados(new Set())}
              disabled={marcados.size === 0}
            >
              Limpar
            </button>
            <button
              type="button" className="btn-primario" style={{ marginLeft: 'auto' }}
              onClick={() => adicionarVinculos(moradores.filter((m) => marcados.has(m.produto_id)))}
              disabled={marcados.size === 0}
            >
              Adicionar {marcados.size > 0 ? `(${marcados.size})` : ''}
            </button>
          </div>
        </div>

        {/* ─── DIREITA: o destino e a leva ─────────────────────────────────────────── */}
        <div style={{ ...PAINEL, flex: '1 1 560px', minWidth: 380 }}>
          {/* Espelho da esquerda (pedido do Ruan, 06/10/2026: "na direita o mesmo formato, so que
              informando o Destino"): faixa com o rotulo e o contador, prateleira em cima, busca
              embaixo, lista, botoes no rodape. O destino morava no rodape ("Levar tudo para") e o
              topo tinha um de-para `— → —` que ele leu como confuso. A origem de cada item segue
              na coluna "Tirar de" e na confirmacao. */}
          <div style={FAIXA}>
            <span style={ROTULO}>Para onde vai</span>
            {leva.length > 0 && (
              <span className="muted" style={{ fontSize: 12, marginLeft: 'auto' }}>
                {leva.length} {leva.length === 1 ? 'produto' : 'produtos'} pra mover
              </span>
            )}
          </div>

          <div style={{ ...CORPO, flex: '0 0 auto', paddingBottom: 0 }}>
            <ComboBox
              opcoes={opcoesDestino}
              value={destinoId}
              onChange={(v) => setDestinoId(v === '' ? null : Number(v))}
              placeholder="Escolha o destino (digite pra buscar)..."
            />
            {/* A busca por codigo e a SEGUNDA entrada da leva ([DA-449], pedido dele): produtos
                espalhados por varias prateleiras. Nao filtra a lista de baixo — adiciona de
                qualquer lugar —, e o placeholder diz isso, porque do lado esquerdo o campo igual
                FILTRA ([DA-586]: o campo existia e ele nao o achava). */}
            <input
              ref={campoCodigo}
              type="search"
              className="busca"
              placeholder="Adicionar de qualquer prateleira: codigo ou descricao + Enter..."
              value={termo}
              onChange={(e) => setTermo(e.target.value)}
              onKeyDown={(e) => {
                // Enter pega a 1a sugestao e limpa o campo pro proximo codigo. E o fluxo de quem
                // esta de pe com a lista na mao: digita, Enter, digita, Enter.
                const primeira = sugestoes[0];
                if (e.key === 'Enter' && primeira) {
                  e.preventDefault();
                  adicionar(primeira);
                  setTermo('');
                  setSugestoes([]);
                }
              }}
              style={{ width: '100%', marginTop: 8 }}
            />
            {termo.trim().length >= 2 && sugestoes.length > 0 && rectBusca && (
              <div style={{
                position: 'fixed', zIndex: 1200, top: rectBusca.bottom + 2, left: rectBusca.left, width: rectBusca.width,
                maxHeight: 240, overflowY: 'auto',
                background: 'var(--panel-bg)', border: '1px solid var(--input-border)', borderRadius: 6,
                boxShadow: '0 6px 20px rgba(0,0,0,.16)',
              }}>
                {sugestoes.map((s) => (
                  <div
                    key={s.produto_id}
                    onClick={() => { adicionar(s); setTermo(''); setSugestoes([]); campoCodigo.current?.focus(); }}
                    style={{ padding: '6px 10px', cursor: 'pointer', display: 'flex', gap: 8, alignItems: 'center' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--hover-bg, rgba(0,0,0,0.06))'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span className="text-gray-500 font-bold no-underline" style={{ minWidth: 44 }}>{s.codigo_interno}</span>
                    <span style={{ flex: 1 }}>{produtoInativo(s) && <SeloInativo />}{s.descricao}<SeloFator item={s} unidade={s.unidade_sigla} /></span>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {s.lugares.length === 0 ? 'sem endereco' : s.lugares.map((l) => `${l.codigo}: ${l.quantidade === null ? '—' : fmt(l.quantidade)}`).join(' · ')}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {buscando && termo.trim().length >= 2 && sugestoes.length === 0 && (
              <p className="muted" style={{ fontSize: 12, marginTop: 4 }}>Procurando...</p>
            )}
          </div>

          <div style={CORPO}>
            {leva.length === 0 ? (
              <Vazio icone={<PackageOpen size={30} />}>
                {/* Marcar a esquerda nao poe na lista — falta o Adicionar. Sem esta frase a tela fica
                    com 9 marcados, destino escolhido e o Transferir apagado sem dizer por que
                    (aconteceu em 06/10/2026). */}
                {marcados.size > 0
                  ? <><strong>{marcados.size}</strong> marcado(s) ao lado ainda nao entraram na lista — clique em <strong>Adicionar</strong>.</>
                  : 'Marque os produtos na prateleira ao lado, ou digite um codigo aqui em cima.'}
              </Vazio>
            ) : (
              <div>
                <table className="tabela" style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 74 }}>Codigo</th>
                      <th style={{ width: 52 }}>Foto</th>
                      <th>Descricao</th>
                      <th style={{ width: 130 }}>Tirar de</th>
                      <th style={{ width: 78, textAlign: 'right' }}>Tem</th>
                      <th style={{ width: 90, textAlign: 'right' }}>Mover</th>
                      <th style={{ width: 34 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {leva.map((it, idx) => {
                      const saldo = saldoDaLinha(it);
                      const q = paraNumero(it.quantidade);
                      const excedeu = Number.isFinite(q) && q > saldo;
                      const vazio = qtdInvalida(q, saldo) && !excedeu;
                      const org = it.origens.find((o) => o.localizacao_id === it.origem_id);
                      return (
                        <tr key={`${it.produto_id}-${it.origem_id}`}>
                          <td className="text-gray-500 font-bold no-underline">{it.codigo_interno}</td>
                          {/* O selo de fator vai numa SEGUNDA LINHA, nunca colado na descricao: a
                              celula corta com reticencias (`DESCRICAO_CURTA`), e selo cortado pela
                              metade e pior que selo nenhum. Como fator e raro (56 produtos em 1.503),
                              a esmagadora maioria das linhas segue com uma linha so. */}
                          <td style={{ padding: '3px 6px' }}>
                            {it.foto_url
                              ? <FotoProduto url={it.foto_url} tam={40} />
                              : <div style={{ width: 40, height: 40, border: '1px dashed var(--border)', borderRadius: 4 }} title="Sem foto" />}
                          </td>
                          <td style={{ maxWidth: 0 }}>
                            {/* `maxWidth: 0` so na CELULA. Repetido no div de dentro (como era), o
                                div nascia com largura zero e a descricao sumia inteira — a coluna
                                saiu vazia em producao ate 06/10/2026. */}
                            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={it.descricao}>
                              {it.descricao}
                              {org?.principal && (
                                <span title="Esta saindo da vitrine deste produto" style={{ background: '#fde68a', color: '#000', borderRadius: 3, padding: '1px 3px', marginLeft: 5, display: 'inline-flex' }}>
                                  <Star size={10} fill="#000" />
                                </span>
                              )}
                            </div>
                            <SeloFator item={it} unidade={it.unidade_sigla} />
                          </td>
                          <td>
                            {it.origens.length > 1 ? (
                              <select value={it.origem_id} onChange={(e) => trocarOrigem(idx, Number(e.target.value))} style={{ width: '100%' }}>
                                {it.origens.map((o) => (
                                  <option key={o.localizacao_id} value={o.localizacao_id}>
                                    {o.codigo} ({fmt(o.quantidade)})
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span>{org?.codigo}</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {fmt(saldo)}
                            {it.unidade_sigla && <span className="muted" style={{ fontSize: 10.5, marginLeft: 3 }}>{it.unidade_sigla}</span>}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <input
                              type="text"
                              inputMode="decimal"
                              className="cel-num"
                              value={it.quantidade}
                              onChange={(e) => setLeva((a) => a.map((x, i) => (i === idx ? { ...x, quantidade: e.target.value } : x)))}
                              onFocus={(e) => e.currentTarget.select()}
                              style={{
                                width: 76, textAlign: 'right', padding: '2px 6px',
                                ...((excedeu || vazio) ? { '--input-bg': '#f97316', '--input-fg': '#000' } as React.CSSProperties : {}),
                              }}
                              title={excedeu ? `${org?.codigo} tem ${fmt(saldo)}` : 'Quanto sai desta prateleira'}
                            />
                          </td>
                          <td>
                            <button
                              type="button" className="btn-secundario" title="Tirar da lista"
                              onClick={() => setLeva((a) => a.filter((_, i) => i !== idx))}
                              style={{ padding: '2px 6px' }}
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Os avisos moram junto da LISTA, nao do botao: e numa linha dela que esta o conserto. */}
            {destinoNaLeva && (
              <p style={{ ...ALERTA, padding: '6px 10px', marginTop: 10, fontSize: 13 }}>
                Ha item saindo de <strong>{lugarDe(destinoId)?.codigo}</strong>, que e o destino escolhido.
                Troque a origem dessa linha ou escolha outro destino.
              </p>
            )}

            {linhasInvalidas.length > 0 && (
              <p style={{ ...ALERTA, padding: '6px 10px', marginTop: 10, fontSize: 13 }}>
                {linhasInvalidas.length} linha(s) com quantidade invalida ou maior do que ha na prateleira.
              </p>
            )}

            {(estrelasQueMudam.length > 0 || linhasParciais.length > 0) && (
              <p style={{ background: '#fde68a', color: '#000', padding: '6px 10px', borderRadius: 3, marginTop: 10, fontSize: 13 }}>
                {estrelasQueMudam.length > 0 && (
                  <>A vitrine (estrela) de <strong>{contar(estrelasQueMudam.length, 'produto')}</strong> passa para {lugarDe(destinoId)?.codigo ?? 'o destino'}. </>
                )}
                {linhasParciais.length > 0 && (
                  <>{contar(linhasParciais.length, 'linha')} com menos do que ha ({linhasParciais.map((e) => e.codigo_interno ?? e.descricao).join(', ')}): o que sobrar continua na prateleira de origem.</>
                )}
              </p>
            )}
          </div>

          {/* ─── rodape: mesmo lugar dos botoes da esquerda ─── */}
          <div style={{ ...RODAPE, flexWrap: 'wrap' }}>
            <button type="button" className="btn-secundario" onClick={() => setLeva([])} disabled={leva.length === 0}>
              Esvaziar
            </button>
            {semDestino && leva.length > 0 && (
              <span className="muted" style={{ fontSize: 12 }}>Escolha o destino no alto do painel pra liberar.</span>
            )}
            {/* Cancelar volta a tela ao comeco (pedido dele, 06/10/2026: "caso nao queira a selecao
                e precise escolher outra"). Esvaziar so tira a lista e mantem as prateleiras. */}
            <button
              type="button" className="btn-secundario" style={{ marginLeft: 'auto' }}
              disabled={enviando || (leva.length === 0 && marcados.size === 0 && destinoId == null && origemLugarId == null)}
              onClick={() => {
                setLeva([]);
                setMarcados(new Set());
                setDestinoId(null);
                setOrigemLugarId(null);
                setFiltroOrigem('');
                setTermo('');
              }}
            >
              Cancelar
            </button>
            <button type="button" className="btn-primario" onClick={transferir} disabled={!podeEnviar}>
              {enviando ? 'Transferindo...' : leva.length > 0 ? `Transferir ${leva.length} ${leva.length === 1 ? 'item' : 'itens'}` : 'Transferir'}
            </button>
          </div>
        </div>
      </div>

      {/* Desfecho em MODAL, nao toast ([DA-443]): o que aconteceu com as vitrines zeradas nao fica
          recuperavel na tela depois — some junto com o toast e o estoquista descobre pela faixa de
          alerta sem saber de onde veio. */}
      {resultado && (
        <Modal
          aberto
          onFechar={() => setResultado(null)}
          titulo="Transferencia concluida"
          largura={620}
          rodape={<button type="button" className="btn-primario" onClick={() => setResultado(null)}>Fechar</button>}
        >
          <p style={{ marginTop: 0 }}>
            <strong>{resultado.movidos.length}</strong> item(ns) movidos para <strong>{resultado.destino_codigo}</strong>.
            {resultado.criados > 0 && ` ${resultado.criados} produto(s) passaram a morar la (endereco novo criado).`}
          </p>

          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--input-border)', borderRadius: 4 }}>
            <table className="tabela" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th style={{ width: 74 }}>Codigo</th>
                  <th>Descricao</th>
                  <th style={{ width: 80 }}>De</th>
                  <th style={{ width: 70, textAlign: 'right' }}>Qtd</th>
                </tr>
              </thead>
              <tbody>
                {resultado.movidos.map((m, i) => (
                  <tr key={`${m.produto_id}-${i}`}>
                    <td className="text-gray-500 font-bold no-underline">{m.codigo_interno}</td>
                    <td>{m.descricao}</td>
                    <td>{m.origem_codigo}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(m.quantidade)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {(resultado.estrelas_movidas?.length ?? 0) > 0 && (
            <p style={{ background: '#fde68a', color: '#000', padding: '8px 12px', borderRadius: 3, marginTop: 12, fontSize: 13, marginBottom: 0 }}>
              A vitrine de <strong>{contar(resultado.estrelas_movidas!.length, 'produto')}</strong> passou para <strong>{resultado.destino_codigo}</strong>.
            </p>
          )}

          <p className="muted" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
            O estoque total dos produtos nao mudou — a mercadoria so trocou de lugar dentro da loja.
          </p>
        </Modal>
      )}
    </div>
  );
}
